/** Pilule de lien affichée dans `HomePopularGrid`. */
export interface HomePopularPill {
  readonly label: string
  readonly slug: string
}

/** Fragment JSON / API `/api/home-popular`. */
export interface HomePopularPayload {
  readonly popularJobs?: readonly HomePopularPill[] | null
  readonly popularSchools?: readonly HomePopularPill[] | null
  readonly popularDiplomas?: readonly HomePopularPill[] | null
  readonly popularSchoolTypes?: readonly HomePopularPill[] | null
  readonly popularCities?: readonly HomePopularPill[] | null
  readonly popularAdvice?: readonly HomePopularPill[] | null
}

/** Données home normalisées après fusion JSON + API. */
export interface HomePopularNormalized {
  readonly popularJobs: readonly HomePopularPill[]
  readonly popularSchools: readonly HomePopularPill[]
  readonly popularDiplomas: readonly HomePopularPill[]
  readonly popularSchoolTypes: readonly HomePopularPill[]
  readonly popularCities: readonly HomePopularPill[]
  readonly popularAdvice: readonly HomePopularPill[]
}

/** État vide avant chargement du fallback. */
export const EMPTY_HOME_POPULAR: HomePopularNormalized = {
  popularJobs: [],
  popularSchools: [],
  popularDiplomas: [],
  popularSchoolTypes: [],
  popularCities: [],
  popularAdvice: [],
}

export const DEFAULT_HOME_POPULAR_FALLBACK_FILE = 'home-popular-fallback.json'

function isNonEmptyArray<T>(v: readonly T[] | null | undefined): v is readonly T[] {
  return Array.isArray(v) && v.length > 0
}

export function cloneHomePopular(data: HomePopularNormalized): HomePopularNormalized {
  return JSON.parse(JSON.stringify(data)) as HomePopularNormalized
}

function normalizePills(
  list: readonly HomePopularPill[] | null | undefined,
): readonly HomePopularPill[] {
  if (!isNonEmptyArray(list)) return []
  return list
    .map((pill: HomePopularPill) => {
      const label: string = pill.label?.trim() ?? ''
      const slug: string = pill.slug?.trim() ?? ''
      if (!label || !slug) return null
      return { label, slug }
    })
    .filter((pill: HomePopularPill | null): pill is HomePopularPill => pill !== null)
}

/**
 * Slug ville pour `/ou-etudier/…` : l'API renvoie souvent `france/lyon` ;
 * le JSON fallback n'a que `lyon` → on préfixe `france/` si absent.
 * @param {string} slug - Segment(s) d'URL ville.
 * @returns {string} Slug sans slash de tête.
 */
function normalizeCityPillSlug(slug: string): string {
  const trimmed: string = slug.replace(/^\/+/, '').replace(/\/+$/, '')
  if (!trimmed) return ''
  if (/^france\//i.test(trimmed)) return trimmed
  return `france/${trimmed}`
}

function normalizeCityPills(
  list: readonly HomePopularPill[] | null | undefined,
): readonly HomePopularPill[] {
  return normalizePills(list).map((pill: HomePopularPill) => ({
    label: pill.label,
    slug: normalizeCityPillSlug(pill.slug),
  }))
}

const FULL_MERGE_NORMALIZERS: {
  [K in keyof HomePopularPayload]-?: (
    v: NonNullable<Exclude<HomePopularPayload[K], null | undefined>>,
  ) => HomePopularNormalized[K]
} = {
  popularJobs: (v) => normalizePills(v),
  popularSchools: (v) => normalizePills(v),
  popularDiplomas: (v) => normalizePills(v),
  popularSchoolTypes: (v) => normalizePills(v),
  popularCities: (v) => normalizeCityPills(v),
  popularAdvice: (v) => normalizePills(v),
}

type FullMergeKey = keyof typeof FULL_MERGE_NORMALIZERS

/**
 * Fusionne un `HomePopularPayload` : chaque tableau API/JSON non vide remplace la clé ;
 * résultat normalisé vide → on conserve la base (évite d’écraser le fallback).
 * @param {HomePopularNormalized} base Données de départ (`EMPTY_HOME_POPULAR` ou déjà fusionné depuis le JSON).
 * @param {HomePopularPayload | null | undefined} raw JSON et/ou fragment API.
 * @returns {HomePopularNormalized} Données fusionnées.
 */
export function mergeHomePopular(
  base: HomePopularNormalized,
  raw: HomePopularPayload | null | undefined,
): HomePopularNormalized {
  if (!raw) return base

  return (Object.keys(FULL_MERGE_NORMALIZERS) as FullMergeKey[]).reduce<HomePopularNormalized>(
    (acc, key) => {
      const value = raw[key]
      if (!isNonEmptyArray(value)) return acc
      const normalized = FULL_MERGE_NORMALIZERS[key](value)
      if (normalized.length === 0) return acc
      return { ...acc, [key]: normalized }
    },
    cloneHomePopular(base),
  )
}
